import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { RainbowButton } from '@/components/ui/rainbow-button';
import { scrollToSection } from '../../utils/helpers';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', target: 'hero' },
    { label: 'Blog', path: '/blog' },
    { label: 'About', target: 'about' },
    { label: 'Reviews', target: 'reviews' },
    { label: 'Contact', target: 'contact' },
  ];

  const serviceLinks = [
    { name: 'AI Consulting', path: '/ai-consultation' },
    { name: 'GEO', path: '/geo' },
  ];

  const handleNavigation = (target: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        if (target === 'hero') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          scrollToSection(target);
        }
      }, 100);
    } else if (target === 'hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollToSection(target);
    }
    closeMenu();
  };

  const handleServiceClick = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    closeMenu();
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/60 backdrop-blur-lg border-b border-white/10 supports-[backdrop-filter]:bg-white/25' 
          : 'bg-transparent'
      } py-4`}
    >
      <div className="container flex items-center">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img 
            src="https://nnhioeyroijmrflqutee.supabase.co/storage/v1/object/public/njordai//NJORD%20W.png" 
            alt="Njord AI Automation Agency Logo" 
            className={`h-28 transition-all duration-300 ${
              isScrolled ? 'opacity-100 brightness-0' : 'opacity-90 hover:opacity-100'
            }`}
          />
        </Link>

        {/* Desktop Navigation - Centered */}
        <nav className="hidden md:flex items-center justify-center flex-grow">
          <div className="flex items-center space-x-12">
            {navLinks.map((link) => (
              link.path ? (
                <button
                  key={link.path}
                  onClick={() => handleServiceClick(link.path)}
                  className={`relative font-medium text-sm tracking-wide transition-colors after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-primary after:transition-all hover:after:w-full ${
                    isScrolled 
                      ? 'text-black hover:text-primary' 
                      : 'text-white/90 hover:text-white after:bg-white'
                  }`}
                >
                  {link.label}
                </button>
              ) : (
                <button
                  key={link.target}
                  onClick={() => handleNavigation(link.target)}
                  className={`relative font-medium text-sm tracking-wide transition-colors after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-primary after:transition-all hover:after:w-full ${
                    isScrolled 
                      ? 'text-black hover:text-primary' 
                      : 'text-white/90 hover:text-white after:bg-white'
                  }`}
                >
                  {link.label}
                </button>
              )
            ))}
            {/* Services Dropdown */}
            <div className="relative group">
              <button
                className={`flex items-center font-medium text-sm tracking-wide transition-colors gap-1 ${
                  isScrolled 
                    ? 'text-black hover:text-primary' 
                    : 'text-white/90 hover:text-white'
                }`}
              >
                Services
                <ChevronDown className="h-4 w-4" />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {serviceLinks.map((service) => (
                  <button
                    key={service.path}
                    onClick={() => handleServiceClick(service.path)}
                    className="block w-full text-center px-4 py-2 text-sm text-gray-700 hover:bg-white/50 transition-colors"
                  >
                    {service.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </nav>

        {/* Book Demo Button */}
        <div className="hidden md:block flex-shrink-0">
          <RainbowButton>
            Book Demo
          </RainbowButton>
        </div>

        {/* Mobile Navigation Toggle */}
        <button 
          className={`md:hidden ml-auto p-2 ${isScrolled ? 'text-black' : 'text-white'}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/80 backdrop-blur-lg z-50 animate-fade-in"
          onClick={closeMenu}
        >
          <div className="px-4 py-20 flex flex-col items-center justify-start space-y-6 h-full overflow-y-auto">
            {navLinks.map((link) => (
              link.path ? (
                <button
                  key={link.path}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleServiceClick(link.path);
                  }}
                  className="text-white/90 hover:text-white text-xl font-medium transition-colors"
                >
                  {link.label}
                </button>
              ) : (
                <button
                  key={link.target}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigation(link.target);
                  }}
                  className="text-white/90 hover:text-white text-xl font-medium transition-colors"
                >
                  {link.label}
                </button>
              )
            ))}
            {/* Mobile Services Links */}
            {serviceLinks.map((service) => (
              <button
                key={service.path}
                onClick={(e) => {
                  e.stopPropagation();
                  handleServiceClick(service.path);
                }}
                className="text-white/90 hover:text-white text-xl font-medium transition-colors"
              >
                {service.name}
              </button>
            ))}
            <div className="mt-6">
              <RainbowButton>
                Book Demo
              </RainbowButton>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;